<template>
  <el-dialog
    custom-class="sharingModal"
    :visible.sync="visible"
    :before-close="close"
    append-to-body
  >
    <h2>Social Platforms</h2>
    <sharing-form-header
      v-model="selectedAccountIds"
      :available-accounts="availableAccounts"
      @renewYoutube="renewYoutube"
      @renewFacebook="renewFacebook"
      @renewInstagram="renewInstagram"
    />

    <div class="sharing-metadata">
      <h2 class="meta-title">Metadata</h2>

      <div v-if="!isAnyAcc">
        No account selected
      </div>

      <template v-else>
        <sharing-youtube-metadata
          v-for="acc in selectedYoutubeAccounts"
          :key="acc.id"
          v-bind="youtubeMetadatas[acc.id]"
          :playlists="acc.playlists"
          @update="updateYoutubeMetadata(acc.id, $event)"
          @invalid="handleYoutubeMetadataInvalid(acc.id)"
        >
          <template v-slot:header>
            YouTube metadata ({{ acc.label }})
          </template>
        </sharing-youtube-metadata>

        <sharing-facebook-metadata
          :class="{ 'hidden-metadata': !isAnyFacebookAcc }"
          v-bind="facebookMetadata"
          @update="handleFacebookMetadataUpdate"
          @invalid="isFacebookMetadataValid = false"
        />

        <sharing-instagram-metadata
          :class="{ 'hidden-metadata': !isAnyInstagramAcc }"
          v-bind="instagramMetadata"
          @update="handleInstagramMetadataUpdate"
          @invalid="isInstagramMetadataValid = false"
        />

        <sharing-twitter-metadata
          :class="{ 'hidden-metadata': !isAnyTwitterAcc }"
          v-bind="twitterMetadata"
          @update="handleTwitterMetadataUpdate"
          @invalid="isTwitterMetadataValid = false"
        />

        <div class="sharing-metadata-block">
          <h3 class="meta-block-title">Schedule</h3>

          <sharing-time-picker v-model="scheduledTime" :timezone="timezone">
            <template v-slot:toggle>Scheduled at</template>
          </sharing-time-picker>
        </div>
      </template>
    </div>

    <div v-if="isFacebookGroupAddedToShare" class="warning-block">
      <span class="warning">
        {{ $options.facebookShareWarning }}
      </span>
    </div>

    <div class="share-buttons">
      <el-button class="btnBackground" :disabled="!isMetadataValidToShare" @click="tryShareVideo">
        Share
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import SharingFormHeader from "@/components/socials/share/SharingFormHeader.vue";
import SharingYoutubeMetadata from "@/components/socials/share/metadata/SharingYoutubeMetadata.vue";
import SharingFacebookMetadata from "@/components/socials/share/metadata/SharingFacebookMetadata.vue";
import SharingInstagramMetadata from "@/components/socials/share/metadata/SharingInstagramMetadata.vue";
import SharingTwitterMetadata from "@/components/socials/share/metadata/SharingTwitterMetadata.vue";
import SharingTimePicker from "@/components/socials/share/metadata/SharingTimePicker.vue";

import { shareVideo } from "@/api/socials/sharings.js";

import { filterArrayByIds } from "@/utils/parsing.js";

import { mapActions, mapState, mapGetters } from "vuex";

// @group Socials
// Dialog window for video sharing to different socials
export default {
  name: "SharingForm",
  components: {
    SharingFormHeader,
    SharingYoutubeMetadata,
    SharingFacebookMetadata,
    SharingInstagramMetadata,
    SharingTwitterMetadata,
    SharingTimePicker
  },
  props: {
    sharingInStudio: {
      type: Boolean,
      default: false
    },
    // Form open flag
    visible: {
      type: Boolean,
      default: true
    },
    // Video identifier
    id: {
      type: Number,
      required: true
    },
    // Video title
    title: {
      type: String,
      default: "No title"
    },
    multistepId: {
      type: Number
    }
  },
  data() {
    return {
      /** Video sharing configurations */
      youtubeMetadatas: {},
      facebookMetadata: this.defaultFacebookMetadata,
      instagramMetadata: this.defaultInstagramMetadata,
      twitterMetadata: this.defaultTwitterMetadata,

      /** are metadatas to share valid */
      youtubeValidStatuses: {},
      isFacebookMetadataValid: true,
      isInstagramMetadataValid: true,
      isTwitterMetadataValid: true,

      /** Accounts to share */
      selectedAccountIds: {
        youtube: [],
        facebook: [],
        instagram: [],
        twitter: []
      },
      // igAccountsWithLimits: [],

      scheduledTime: null
    };
  },
  facebookShareWarning: `Note: you need to add Mangomolo DVR Clipper 
                        application in your Facebook group settings 
                        before sharing video to it`,
  computed: {
    ...mapState({
      youtubeAccounts: state => state.socials.youtube.accounts,
      facebookAccounts: state => state.socials.facebook.accounts,
      instagramAccounts: state => state.socials.instagram.accounts,
      twitterAccounts: state => state.socials.twitter.accounts,
      facebookGroupsInited: state => state.socials.facebook.groupsInited,
      facebookPagesInited: state => state.socials.facebook.pagesInited,
      instagramInited: state => state.socials.instagram.inited,
      timezone: state => state.timezone.timezone
    }),
    ...mapGetters("socials", {
      getYoutubeAcc: "getYoutubeAccountById",
      getFacebookAcc: "getFacebookAccountById",
      getInstagramAcc: "getInstagramAccountById",
      getTwitterAcc: "getTwitterAccountById"
    }),
    availableAccounts() {
      return {
        youtube: this.youtubeAccounts,
        facebook: this.facebookAccounts,
        instagram: this.igAccountsWithLimits,
        twitter: this.twitterAccounts
      };
    },
    igAccountsWithLimits() {
      return this.instagramAccounts.map(acc => {
        const limitedLabel = `${acc.label} (${acc.quota.usage}/${acc.quota.total})`;
        const newAcc = {
          ...acc,
          label: limitedLabel
        };
        return newAcc;
      });
    },
    selectedYoutubeIds() {
      return this.selectedAccountIds.youtube;
    },
    selectedYoutubeAccounts() {
      return this.selectedYoutubeIds.map(id =>
        this.availableAccounts.youtube.find(acc => acc.id === id)
      );
    },
    // @vuese
    // Facebook group objects array to share video
    facebookGroupsToShare() {
      const facebookGroups = this.selectedAccountIds.facebook
        .map(fullId => fullId.split("_"))
        .filter(([, dest]) => dest == "group")
        .map(([accountId, , id]) => ({ accountId, id }));

      return facebookGroups;
    },
    // @vuese
    // Facebook page objects array to share video
    facebookPagesToShare() {
      const facebookPages = this.selectedAccountIds.facebook
        .map(fullId => fullId.split("_"))
        .filter(([, dest]) => dest == "page")
        .map(([accountId, , id]) => ({ accountId, id }));

      return facebookPages;
    },
    // @vuese
    // Instagram page objects array to share video
    instagramPagesToShare() {
      const sharingInstagramPages = filterArrayByIds(
        [...this.instagramAccounts],
        this.selectedAccountIds.instagram,
        acc => acc.id
      );

      return sharingInstagramPages;
    },
    // @vuese
    // Twitter account objects array to share video
    twitterAccountsToShare() {
      const sharingTwitterAccounts = filterArrayByIds(
        [...this.twitterAccounts],
        this.selectedAccountIds.twitter,
        acc => acc.id
      );

      return sharingTwitterAccounts;
    },
    // @vuese
    // Is any account added to share
    isAnyAcc() {
      return Object.values(this.selectedAccountIds).some(ids => ids.length > 0);
    },
    // @vuese
    // Is any YouTube account iss selected
    isAnyYotubeAcc() {
      return this.selectedYoutubeIds.length > 0;
    },
    // @vuese
    // Is any Facebook account iss selected
    isAnyFacebookAcc() {
      return this.selectedAccountIds.facebook.length > 0;
    },
    // @vuese
    // Is any Instagram account iss selected
    isAnyInstagramAcc() {
      return this.selectedAccountIds.instagram.length > 0;
    },
    // @vuese
    // Is any Twitter account iss selected
    isAnyTwitterAcc() {
      return this.selectedAccountIds.twitter.length > 0;
    },
    // @vuese
    // Is it available to perform current sharing to YouTube
    isYotubeValidToShare() {
      return this.selectedYoutubeIds.every(id => this.youtubeValidStatuses[id]);
    },
    // @vuese
    // Is it available to perform current sharing to Facebook
    isFacebookValidToShare() {
      return this.isFacebookMetadataValid || !this.isAnyFacebookAcc;
    },
    // @vuese
    // Is it available to perform current sharing to Instagram
    isInstagramValidToShare() {
      return this.isInstagramMetadataValid || !this.isAnyInstagramAcc;
    },
    // @vuese
    // Is it available to perform current sharing to Twitter
    isTwitterMetadataValidToShare() {
      return this.isTwitterMetadataValid || !this.isAnyTwitterAcc;
    },
    // @vuese
    // Is video can be shared with current settings
    isMetadataValidToShare() {
      return (
        this.isAnyAcc &&
        this.isYotubeValidToShare &&
        this.isFacebookValidToShare &&
        this.isInstagramValidToShare &&
        this.isTwitterMetadataValidToShare
      );
    },
    // @vuese
    // Is there facebook group in a selected destinations
    isFacebookGroupAddedToShare() {
      const accounts = this.selectedAccountIds.facebook;
      for (let i = 0; i < accounts.length; i++) {
        const accountId = accounts[i];
        const accountType = accountId.split("_")[1];

        if (accountType === "group") {
          return true;
        }
      }

      return false;
    },
    // @vuese
    // YouTube metadata used by default
    defaultYoutubeMetadata() {
      return {
        title: this.title,
        categoryId: "1",
        description: "",
        isPublic: false,
        tags: [],
        playlistId: null
      };
    },
    // @vuese
    // Facebook metadata used by default
    defaultFacebookMetadata() {
      return {
        title: this.title,
        description: ""
      };
    },
    // @vuese
    // Instagram metadata used by default
    defaultInstagramMetadata() {
      return {
        caption: ""
      };
    },
    // @vuese
    // Twitter metadata used by default
    defaultTwitterMetadata() {
      return {
        text: "Tweet something"
      };
    }
  },
  watch: {
    // @vuese
    // Initialize dahring data when modal is opened
    visible() {
      if (this.visible) {
        this.initAllYoutubePlaylists();
        this.facebookAccounts.forEach(account => {
          if (!this.instagramInited) {
            this.initAllInstagramPages(account);
          }
          if (!this.facebookGroupsInited || !this.facebookPagesInited) {
            this.initAllFacebookGroups(account);
            this.initAllFacebookPages(account);
          }
        });
      }
    },
    // @vuese
    // Update title if user doesnt change it
    title(newTitle, oldTitle) {
      for (const id in this.youtubeMetadatas) {
        if (this.youtubeMetadatas[id].title === oldTitle) {
          this.updateYoutubeMetadata(id, {
            ...this.youtubeMetadatas[id],
            title: newTitle
          });
        }
      }

      if (this.facebookMetadata.title === oldTitle) {
        this.facebookMetadata.title = newTitle;
      }
    },
    selectedYoutubeIds(newIds, oldIds) {
      const oldIdsSet = new Set(oldIds);

      /** init metadata for new accounts */
      for (const id of newIds) {
        if (!oldIdsSet.has(id) && !(id in this.youtubeMetadatas)) {
          this.updateYoutubeMetadata(id, { ...this.defaultYoutubeMetadata });
        }
      }
    }
  },
  // @vuese
  // Set metadata to default when object has created
  async created() {
    this.clearSharingForm();
    await this.updateAccounts("youtube");
    this.initAllYoutubePlaylists();
    await this.updateAccounts("facebook");
    this.initAllFacebookGroups();
    this.initAllFacebookPages();
    await this.initAllInstagramPages();
  },
  methods: {
    ...mapActions("socials", [
      "updateAccounts",
      "initAllYoutubePlaylists",
      "initAllFacebookGroups",
      "initAllFacebookPages",
      "initAllInstagramPages"
    ]),
    // @vuese
    // Close form
    close() {
      // Form closed
      if (!this.sharingInStudio) {
        this.$emit("closed");
      }
    },
    // @vuese
    // Send request to update socials list
    async renewYoutube() {
      await this.updateAccounts("youtube");
      this.initAllYoutubePlaylists();
    },
    // @vuese
    // Send request to update facebook accounts, groups and pages
    async renewFacebook() {
      await this.updateAccounts("facebook");
      this.initAllFacebookGroups();
      this.initAllFacebookPages();
    },
    // @vuese
    // Send request to update Instagram pages
    async renewInstagram() {
      await this.initAllInstagramPages();
    },
    // @vuese
    // Set YouTube metadata and reset invalid flag
    // @arg id - account ID <br>
    // @arg metadata - new YouTube metadata value
    updateYoutubeMetadata(id, newMetadata) {
      /** use destruction to init Vue reactivity */
      this.youtubeMetadatas = {
        ...this.youtubeMetadatas,
        [id]: newMetadata
      };
      this.youtubeValidStatuses = {
        ...this.youtubeValidStatuses,
        [id]: true
      };
    },
    // @vuese
    // Set invaid flag
    // @arg id - account ID
    handleYoutubeMetadataInvalid(id) {
      /** use destruction to init Vue reactivity */
      this.youtubeValidStatuses = {
        ...this.youtubeValidStatuses,
        [id]: false
      };
    },
    async renewTwitter() {
      await this.updateAccounts("twitter");
    },
    // @vuese
    // Set Facebook metadata and reset invalid flag
    // @arg metadata - new Facebook metadata value
    handleFacebookMetadataUpdate(metadata) {
      this.facebookMetadata = metadata;
      this.isFacebookMetadataValid = true;
    },
    // @vuese
    // Set Instagram metadata and reset invalid flag
    // @arg metadata - new Instagram metadata value
    handleInstagramMetadataUpdate(metadata) {
      this.instagramMetadata = metadata;
      this.isInstagramMetadataValid = true;
    },
    // @vuese
    // Set Twitter metadata and reset invalid flag
    // @arg metadata - new Twitter metadata value
    handleTwitterMetadataUpdate(metadata) {
      this.twitterMetadata = metadata;
      this.isTwitterMetadataValid = true;
    },
    // @vuese
    // Handle video sharing request
    async tryShareVideo() {
      if (!this.isMetadataValidToShare) return;

      if (!this.isValidSharingSchedule()) {
        this.showError("Invalid sharing schedule");
        return;
      }

      const sharingResults = await this.shareVideo();
      const fullSuccess = this.handleSharingResults(sharingResults);

      if (fullSuccess) {
        this.close();
        this.clearSharingForm();
        this.showSuccess("Video sharing has started");
      } else {
        this.$alert(`Video sharing is failed for some accounts`, {
          confirmButtonText: "Ok"
        });
      }
      if (this.sharingInStudio) {
        this.$emit("closed");
      }
    },
    // @vuese
    // Handle sharing results by removing shared accounts from form
    // @arg results - all results from Promise.allSettled
    handleSharingResults(results) {
      let isAnyFailed = false;

      for (const { status, value, reason } of results) {
        if (status == "fulfilled") {
          this.unselectAccount(value.social, value.id);
        } else {
          this.showError(reason);
          isAnyFailed = true;
        }
      }
      return !isAnyFailed;
    },
    // @vuese
    // Share video to all accounts selected by user
    async shareVideo() {
      const { youtube, instagram, twitter } = this.selectedAccountIds;
      const facebookGroups = this.facebookGroupsToShare;
      const facebookPages = this.facebookPagesToShare;

      const youtubeDests = youtube.map(this.getYoutubeAcc).map(account => ({
        account,
        id: account.id,
        metadata: this.youtubeMetadatas[account.id]
      }));

      const getFacebookDestinations = (source, type) => {
        return source.map(({ id, accountId }) => ({
          account: this.getFacebookAcc(accountId),
          id,
          type,
          metadata: this.facebookMetadata
        }));
      };

      const facebookDests = [
        ...getFacebookDestinations(facebookGroups, "groups"),
        ...getFacebookDestinations(facebookPages, "page")
      ];

      const instagramDests = instagram
        .map(this.getInstagramAcc)
        .map(account => ({
          account,
          id: account.id,
          metadata: this.instagramMetadata
        }));

      const twitterDests = twitter.map(this.getTwitterAcc).map(account => ({
        account,
        id: account.id,
        metadata: this.twitterMetadata
      }));

      const socialDests = [
        ...youtubeDests,
        ...facebookDests,
        ...instagramDests,
        ...twitterDests
      ];

      if (this.scheduledTime) {
        socialDests.forEach(dest => {
          dest.metadata.scheduledTime = this.scheduledTime;
        });
      }

      const shareResults = await shareVideo(
        this.id,
        socialDests,
        this.multistepId
      );
      return shareResults;
    },
    // @vuese
    // Remove account from selected accounts list
    // @arg social - social name (youtube/facebook/instagram/twitter) <br>
    // @arg id - account unique id
    unselectAccount(social, id) {
      let accountIndex = -1;

      if (social === "facebook") {
        accountIndex = this.selectedAccountIds.facebook.findIndex(
          accId => accId.split("_")[2] == id
        );
      } else {
        accountIndex = this.selectedAccountIds[social].indexOf(id);
      }

      if (accountIndex < 0) {
        throw new Error(`Account with ID '${id}' is not found`);
      }

      this.selectedAccountIds[social].splice(accountIndex, 1);
    },
    // @vuese
    // Erase entered data from sharing form
    clearSharingForm() {
      this.youtubeMetadatas = {};
      this.facebookMetadata = this.defaultFacebookMetadata;
      this.instagramMetadata = this.defaultInstagramMetadata;
      this.twitterMetadata = this.defaultTwitterMetadata;
    },
    // @vuese
    // Is every scheduled sharing time valid
    isValidSharingSchedule() {
      return !this.scheduledTime || +this.scheduledTime > Date.now();
    },
    // @vuese
    // Show error notification
    showError(title, error) {
      this.$notify({
        group: "foo",
        position: "bottom right",
        type: "error",
        title: title,
        text: error
      });
    },
    // @vuese
    // Show success notification
    showSuccess(title, message) {
      this.$notify({
        group: "foo",
        position: "bottom right",
        type: "success",
        title: title,
        text: message
      });
    }
  }
};
</script>

<style lang="scss">

.sharingModal {
  text-align: center;
  border-radius: 5px !important;
  margin-top: 5vh !important;
  background-color: #233d4e;
}
.el-dialog__close.el-icon.el-icon-close {
  color: white;
}
.sharing-icon {
  width: 20px;
  height: 20px;
}
.sharing-link {
  width: 20px;
  height: 20px;
  padding: 3px;
}
.enqued-link {
  opacity: 0.5;
}
.converted-link img {
  cursor: pointer !important;
}
.process-link,
.success-link {
  /*animation: load-sharing 2s infinite;*/
  overflow: hidden;
  animation-duration: 1.5s;
  animation-name: load-sharing;
  animation-iteration-count: infinite;
  animation-direction: normal;
}
.error-link img {
  opacity: 0.3;
  border: 2px solid rgba(255, 0, 0, 1);
}
.sharing-metadata {
  height: 55vh;
  overflow-y: auto;
  padding: 10px;
  margin: 10px;
  border: 2px solid #5d5d5d;
  border-left: none;
  border-right: none;
}
h2.meta-title {
  margin-bottom: 20px;
}
h3.meta-block-title {
  margin-bottom: 5px;
}
.sharing-metadata-block {
  margin-bottom: 40px;
  overflow: hidden;
  /** max height was need to smooth hide/display animation, but it will broke displaying */
  /** max-height: 500px; 
  transition: max-height 0.5s ease-out, margin-bottom 0.5s ease-out; */
}
.sharing-metadata-block.hidden-metadata {
  max-height: 0px;
  margin-bottom: 0;
}
.metadata-row {
  display: flex;
}
.metadata-row-label {
  margin-right: 15px;
}
.youtube-title,
.facebook-title {
  width: 50%;
  margin-right: 10px;
}
.youtube-category,
.facebook-category {
  /*margin-left: 10px;*/
  margin-right: 10px;
  width: 30%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.margin-top {
  margin-top: 10px;
}
.youtube-privacy,
.facebook-story {
  width: 20%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.warning-block {
  margin: 15px 0;
}
.warning {
  font-style: italic;
  color: #ffa;
}

.btnBackground{
  background: #ed5229;
}

/* Sharing modal scrollbar */
/* width */
.sharingModal ::-webkit-scrollbar {
  width: 15px;
}

/* Track */
.sharingModal ::-webkit-scrollbar-track {
  background: #777;
}

/* Handle */
.sharingModal ::-webkit-scrollbar-thumb {
  background: #555;
  border-radius: 10px;
}

/* Handle on hover */
.sharingModal ::-webkit-scrollbar-thumb:hover {
  background: #444;
  border-radius: 10px;
}
@media only screen and (max-width: 767px) {
  .sharingModal {
    width: 90%;
  }
  .metadata-row {
    flex-wrap: wrap;
  }
  .el-form-item {
    min-width: 150px;
  }
  .youtube-category {
    margin: 0;
  }
}

@media only screen and (min-width: 768px) {
  .sharingModal {
    width: 70%;
  }
}

@media only screen and (min-width: 1201px) {
  .sharingModal {
    width: 50%;
  }
}

.sharingModal input {
  background-color: #aaaaaa;
}

.el-tabs__content {
  overflow-y: auto !important;
}

.youtube-description__input .el-textarea__inner {
  font-family: 'POPPINS';
  background-color: #aaaaaa;
}

/* Public option */
.youtube-public__button.el-checkbox-button.is-disabled:first-child
  .el-checkbox-button__inner {
  background-color: var(--dark-grey) !important;
  border: none;
}

.el-button.is-disabled,
.el-button.is-disabled:hover,
.el-button.is-disabled:focus {
  background-color: var(--dark-grey) !important;
  border: none;
}

/* Tags styles */
.el-tag.el-tag--info {
  color: white !important;
}
.tag-input {
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;
  flex-wrap: wrap;
  width: 100%;
  position: static;
  box-sizing: border-box;
}
.tag-input__tag {
  margin-bottom: 22px;
  margin-right: 10px;
  padding: 0 10px;
  height: 40px;
  line-height: 40px !important;
  font-size: 1em !important;
  background-color: #5d5d5d;
  color: white;
  border-radius: 5px;
}
.tag-input__tag > span {
  cursor: pointer;
}
.tag-input .el-form-item {
  width: 130px;
}
.tag-input .el-form-item__content {
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  width: 100%;
  box-sizing: border-box;
}
.tag-input__text {
  padding: 10px;
  padding-left: 0;
  height: 30px;
  width: 110px;
}

.el-input.el-input--suffix .el-input__inner{  background-color: #aaaaaa;}
.el-input.tag-input__text .el-input__suffix-inner {
  position: relative;
  top: 35px;
}
.el-input.tag-input__text .el-input__count .el-input__count-inner {
  color: white !important;
  background-color: inherit !important;
}
</style>
